import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ToastContainer } from 'react-toastify';

const PopoutWindow = ({
    children,
    title = 'Window',
    width,
    height,
    name,
    onClose,
    toastContainerId,
    zoom = 1
}) => {
    const [container, setContainer] = useState(null);
    const popupRef = useRef(null);
    const closeNotifiedRef = useRef(false);
    const onCloseRef = useRef(onClose);

    useEffect(() => {
        onCloseRef.current = onClose;
    }, [onClose]);

    useEffect(() => {
        // Match the old in-page modal sizing by default.
        const modalLikeWidth = Math.min(1300, Math.floor(window.innerWidth * 0.95));
        const modalLikeHeight = Math.floor(window.innerHeight * 0.75);
        const desiredWidth = width || modalLikeWidth;
        const desiredHeight = height || modalLikeHeight;

        const screenWidth = window.screen.availWidth;
        const screenHeight = window.screen.availHeight;
        const windowWidth = Math.min(desiredWidth, Math.floor(screenWidth * 0.95));
        const windowHeight = Math.min(desiredHeight, Math.floor(screenHeight * 0.92));
        const left = Math.max(0, Math.floor((screenWidth - windowWidth) / 2));
        const top = Math.max(0, Math.floor((screenHeight - windowHeight) / 2) - 30);

        const features = [
            `width=${windowWidth}`,
            `height=${windowHeight}`,
            `left=${left}`,
            `top=${top}`,
            'resizable=yes',
            'scrollbars=yes',
            'status=no',
            'menubar=no',
            'toolbar=no',
            'location=no',
            'directories=no',
            'personalbar=no',
            'copyhistory=no',
            'dependent=yes'
        ].join(',');

        const popup = window.open('', name || `payable_window_${Date.now()}`, features);
        if (!popup) {
            alert('Popup blocked! Please allow popups for this site.');
            if (onCloseRef.current) onCloseRef.current();
            return undefined;
        }

        popupRef.current = popup;
        popup.document.title = title;

        // Copy current styles so the form looks identical in the popup.
        const styleNodes = document.querySelectorAll('link[rel="stylesheet"], style');
        styleNodes.forEach((node) => {
            popup.document.head.appendChild(node.cloneNode(true));
        });

        popup.document.body.style.margin = '0';
        popup.document.body.style.background = '#f5f5f5';
        popup.document.body.style.fontFamily = "'Arial', sans-serif";

        const rootEl = popup.document.createElement('div');
        rootEl.style.minHeight = '100vh';
        rootEl.style.overflow = 'auto';
        if (zoom !== 1) rootEl.style.zoom = zoom;
        popup.document.body.style.overflow = 'auto';
        popup.document.body.appendChild(rootEl);
        setContainer(rootEl);

        const notifyClosed = () => {
            if (closeNotifiedRef.current) return;
            closeNotifiedRef.current = true;
            if (onCloseRef.current) onCloseRef.current();
        };

        const handleBeforeUnload = () => notifyClosed();
        popup.addEventListener('beforeunload', handleBeforeUnload);

        // Bridge popup user activity to opener so session timeout resets while popup is in focus.
        const forwardActivityToMain = () => {
            ['mousemove', 'keydown', 'mousedown', 'touchstart'].forEach((eventName) => {
                window.dispatchEvent(new Event(eventName));
            });
        };
        const activityEvents = ['mousemove', 'keydown', 'mousedown', 'touchstart', 'focus'];
        activityEvents.forEach((eventName) => popup.addEventListener(eventName, forwardActivityToMain));

        const intervalId = window.setInterval(() => {
            if (!popup || popup.closed) {
                window.clearInterval(intervalId);
                notifyClosed();
            }
        }, 500);

        return () => {
            window.clearInterval(intervalId);
            popup.removeEventListener('beforeunload', handleBeforeUnload);
            activityEvents.forEach((eventName) => popup.removeEventListener(eventName, forwardActivityToMain));
            if (!popup.closed) popup.close();
        };
    }, [height, name, width]);

    useEffect(() => {
        if (popupRef.current && !popupRef.current.closed) {
            popupRef.current.document.title = title;
        }
    }, [title]);

    if (!container) return null;
    return createPortal(
        <>
            {children}
            {toastContainerId && (
                <ToastContainer
                    containerId={toastContainerId}
                    position="top-right"
                    autoClose={5000}
                    hideProgressBar={false}
                    newestOnTop={false}
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                />
            )}
        </>,
        container
    );
};

export default PopoutWindow;
